import React, { useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material'
import { LocalizationProvider, TimePicker, DatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import 'dayjs/locale/pt'
import 'dayjs/locale/fr'
import 'dayjs/locale/en'

import BaseDialog from '../base-dialog'
import AutocompleteInput from '../autocomplete-input'
import { useNotificationsProvider } from '../../context/NotificationsContext'

ScheduleInspectionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onScheduled: PropTypes.func.isRequired,
  defaultDate: PropTypes.instanceOf(Date),
  controllers: PropTypes.array,
  loadingControllers: PropTypes.bool,
  scheduleInspectionFunction: PropTypes.func.isRequired,
}

export default function ScheduleInspectionDialog({
  open,
  onClose,
  onScheduled,
  defaultDate = null,
  controllers = [],
  loadingControllers = false,
  scheduleInspectionFunction,
}) {
  const { t } = useTranslation()
  const { showNotification } = useNotificationsProvider()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [selectedResource, setSelectedResource] = useState(null)
  const [selectedFlow, setSelectedFlow] = useState(null)
  const [selectedControllerId, setSelectedControllerId] = useState('')
  const [controlDate, setControlDate] = useState(dayjs())

  // Get locale from browser and set dayjs locale
  const locale = useMemo(() => {
    const browserLocale = (navigator.language || navigator.userLanguage).substring(0, 2)
    if (['pt', 'fr', 'en'].includes(browserLocale)) {
      dayjs.locale(browserLocale)
      return browserLocale
    }
    dayjs.locale('en')
    return 'en'
  }, [])

  const resetState = () => {
    setLoading(false)
    setError('')
    setSelectedResource(null)
    setSelectedFlow(null)
    setSelectedControllerId('')
    setControlDate(defaultDate ? dayjs(defaultDate) : dayjs())
  }

  // Set date when dialog opens with defaultDate
  useEffect(() => {
    if (open && defaultDate) {
      let dateValue = dayjs(defaultDate)
      // If the time is midnight (00:00), use current time instead
      // This happens when clicking on a day cell rather than a specific time slot
      if (dateValue.hour() === 0 && dateValue.minute() === 0) {
        const now = dayjs()
        dateValue = dateValue.hour(now.hour()).minute(now.minute())
      }
      setControlDate(dateValue)
    }
  }, [open, defaultDate])

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setError('')
      setSelectedResource(null)
      setSelectedFlow(null)
      setSelectedControllerId('')
    }
  }, [open])

  const onCloseDialog = (event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      onClose()
    } else {
      resetState()
      onClose()
    }
  }

  const onConfirm = async () => {
    // Validate required fields
    if (!selectedResource) {
      setError('resource_required')
      showNotification('error', t('schedule-inspection-dialog.errors.resource-required'))
      return
    }

    if (!selectedFlow) {
      setError('flow_required')
      showNotification('error', t('schedule-inspection-dialog.errors.flow-required'))
      return
    }

    if (!selectedControllerId) {
      setError('controller_required')
      showNotification('error', t('schedule-inspection-dialog.errors.controller-required'))
      return
    }

    if (!controlDate) {
      setError('date_required')
      showNotification('error', t('schedule-inspection-dialog.errors.date-required'))
      return
    }

    setLoading(true)
    setError('')

    try {
      const controlDateISOString = controlDate.toISOString()

      await scheduleInspectionFunction({
        resourceId: selectedResource.id,
        flowId: selectedFlow.id,
        assignedToUserId: selectedControllerId,
        controlDate: controlDateISOString,
      })

      onScheduled()
      resetState()
      onClose()
    } catch (err) {
      setLoading(false)
      console.error('Error scheduling inspection:', err)

      // Handle specific error codes
      if (err.response?.data?.errorCode) {
        const { errorCode } = err.response.data
        if (errorCode === 'RESOURCE_NOT_FOUND') {
          setError('resource_not_found')
          showNotification('error', t('schedule-inspection-dialog.errors.resource-not-found'))
        } else if (errorCode === 'FLOW_NOT_FOUND') {
          setError('flow_not_found')
          showNotification('error', t('schedule-inspection-dialog.errors.flow-not-found'))
        } else if (errorCode === 'USER_NOT_FOUND') {
          setError('controller_not_found')
          showNotification('error', t('schedule-inspection-dialog.errors.controller-not-found'))
        } else if (errorCode === 'PAST_DATE_NOT_ALLOWED') {
          setError('past_date')
          showNotification('error', t('schedule-inspection-dialog.errors.past-date'))
        } else {
          setError('api_error')
          showNotification('error', t('schedule-inspection-dialog.errors.api-error'))
        }
      } else {
        setError('api_error')
        showNotification('error', t('schedule-inspection-dialog.errors.api-error'))
      }
    }
  }

  return (
    <BaseDialog
      open={open}
      onClose={onCloseDialog}
      onConfirm={onConfirm}
      title={t('schedule-inspection-dialog.title')}
      loading={loading}
      openButtonTitle={t('schedule-inspection-dialog.confirm')}
      width="sm"
    >
      <Grid container direction="column" spacing={3} sx={{ mt: 1 }}>
        {/* Resource Selector */}
        <Grid item>
          <AutocompleteInput
            resource="resource/name-autocomplete"
            label={t('schedule-inspection-dialog.resource')}
            value={selectedResource}
            onChange={(newValue) => {
              setSelectedResource(newValue)
              if (error === 'resource_required' || error === 'resource_not_found') {
                setError('')
              }
            }}
            required
            error={error === 'resource_required' || error === 'resource_not_found'}
            refreshOnInputChange
            canDelete
          />
        </Grid>

        {/* Flow Selector */}
        <Grid item>
          <AutocompleteInput
            resource="flows/autocomplete"
            label={t('schedule-inspection-dialog.flow')}
            value={selectedFlow}
            onChange={(newValue) => {
              setSelectedFlow(newValue)
              if (error === 'flow_required' || error === 'flow_not_found') {
                setError('')
              }
            }}
            required
            error={error === 'flow_required' || error === 'flow_not_found'}
            refreshOnInputChange
            canDelete
          />
        </Grid>

        {/* Controller Selector */}
        <Grid item>
          <FormControl
            fullWidth
            variant="outlined"
            required
            error={error === 'controller_required' || error === 'controller_not_found'}
          >
            <InputLabel id="controller-select-label">
              {t('schedule-inspection-dialog.controller')}
            </InputLabel>
            <Select
              labelId="controller-select-label"
              id="controller-select"
              value={selectedControllerId}
              onChange={(event) => {
                setSelectedControllerId(event.target.value)
                if (error === 'controller_required' || error === 'controller_not_found') {
                  setError('')
                }
              }}
              label={t('schedule-inspection-dialog.controller')}
              disabled={loadingControllers}
            >
              {controllers.map((controller) => (
                <MenuItem key={controller.id} value={controller.id}>
                  {controller.firstName} {controller.lastName}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{t('common.required')}</FormHelperText>
          </FormControl>
        </Grid>

        {/* Date and Time Pickers */}
        <Grid item container spacing={2}>
          <Grid item xs={7}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
              <DatePicker
                label={t('schedule-inspection-dialog.date')}
                value={controlDate}
                onChange={(newDate) => {
                  if (newDate && controlDate) {
                    // Preserve the time when changing the date
                    const updatedDate = newDate.hour(controlDate.hour()).minute(controlDate.minute())
                    setControlDate(updatedDate)
                  } else {
                    setControlDate(newDate)
                  }
                  if (error === 'date_required' || error === 'past_date') {
                    setError('')
                  }
                }}
                minDate={dayjs()}
                slotProps={{
                  textField: {
                    variant: 'standard',
                    fullWidth: true,
                    required: true,
                    error: error === 'date_required' || error === 'past_date',
                  },
                  desktopPaper: {
                    sx: {
                      '& .MuiPickersDay-root.Mui-selected': {
                        backgroundColor: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                      },
                      '& .MuiPickersDay-root:focus.Mui-selected': {
                        backgroundColor: 'primary.main',
                      },
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={5}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
              <TimePicker
                label={t('schedule-inspection-dialog.time')}
                value={controlDate}
                onChange={(newTime) => {
                  if (newTime && controlDate) {
                    // Update only the time, keep the date
                    const updatedDate = controlDate.hour(newTime.hour()).minute(newTime.minute())
                    setControlDate(updatedDate)
                  } else if (newTime) {
                    setControlDate(newTime)
                  }
                }}
                ampm={false}
                minutesStep={15}
                slotProps={{
                  textField: {
                    variant: 'standard',
                    fullWidth: true,
                    error: false,
                  },
                  desktopPaper: {
                    sx: {
                      '& ul': {
                        '&::-webkit-scrollbar': {
                          width: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'rgba(0,0,0,0.2)',
                          borderRadius: '4px',
                        },
                      },
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </Grid>
    </BaseDialog>
  )
}
